import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCustomEventEmitter } from '@/hook';

import {
  DEFAULT_TRACK_MAX,
  DEFAULT_TRACK_MIN,
  DEFAULT_TRACK_STEP,
  TRACK_TYPE,
} from './constant';
import BaseTrackContext from './context';
import type { VBaseTrackProps, VBaseTrackType } from './type';
import {
  getDefaultValue,
  offsetRateByFactory,
  valueByClampFactory,
} from './util';

const VBaseTrackContextProvider = <
  T extends VBaseTrackType = TRACK_TYPE.NORMAL,
>({
  children,
  max = DEFAULT_TRACK_MAX,
  min = DEFAULT_TRACK_MIN,
  step = DEFAULT_TRACK_STEP,
  type = TRACK_TYPE.NORMAL as T,
  value,
  onChange,
  ...rest
}: React.PropsWithChildren<VBaseTrackProps<T>>) => {
  const [innerStep, setInnerStep] = useState<VBaseTrackProps<T>['step']>(step);
  const [innerValue, setInnerValue] = useState<VBaseTrackProps<T>['value']>();
  const { eventEmitter } = useCustomEventEmitter({
    onChange: onChange ?? (() => {}),
  });
  const $track = useRef<HTMLDivElement>(null);
  const getClampedValue = valueByClampFactory({ max, min });
  const getOffsetRate = offsetRateByFactory({ max, min });

  const offsetRate = useMemo(() => {
    switch (type) {
      case TRACK_TYPE.RANGE:
        return ((innerValue ?? []) as [number, number])
          .toSorted((a, b) => a - b)
          .map(getOffsetRate);
      case TRACK_TYPE.MULTIPLE_RANGE:
        return ((innerValue ?? []) as [number, number][]).map(row =>
          row.toSorted((a, b) => a - b).map(getOffsetRate),
        );
      default:
        return getOffsetRate(innerValue as number);
    }
  }, [getOffsetRate, innerValue, type]);

  const setValue = (newValue: VBaseTrackProps<T>['value']) => {
    setInnerValue(() => {
      switch (type) {
        case TRACK_TYPE.RANGE:
          return (newValue as [number, number])
            .toSorted((a, b) => a - b)
            .map(getClampedValue) as [number, number];
        case TRACK_TYPE.MULTIPLE_RANGE:
          return (newValue as [number, number][]).map(row =>
            row.toSorted((a, b) => a - b).map(getClampedValue),
          ) as [number, number][];
        default:
          return getClampedValue((newValue as number) ?? min);
      }
    });
  };

  const setStep = (newStep: VBaseTrackProps<T>['step']) => {
    setInnerStep(newStep);
  };

  useEffect(() => {
    if (isEqual(value, innerValue)) return;

    setValue(value ?? getDefaultValue(type, min));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, value, type]);

  useEffect(() => {
    if (isEqual(value, innerValue) || innerValue == null) return;

    eventEmitter.current.emit('change', { value: innerValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerValue]);

  return (
    <BaseTrackContext.Provider
      value={{
        ...rest,
        eventEmitter: eventEmitter.current,
        max,
        min,
        step: innerStep,
        offsetRate,
        type,
        value: innerValue,
        $track,

        setStep,
        setValue,
      }}
    >
      {children}
    </BaseTrackContext.Provider>
  );
};

VBaseTrackContextProvider.displayName = 'VBaseTrackContextProvider';

export default VBaseTrackContextProvider;
